/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box, Typography, Chip, Button, Grid } from "@mui/material";
import { format } from "date-fns";
import ManagerTaskCard from "./manager-task-card";
import { useSelector } from "react-redux";
import Loading from "../../../common/loader/loading";

const ManagerTaskList = () => {
  const user = useSelector((state) => state.auth.user);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null); // Track the selected task
  const [timelineFilter, setTimelineFilter] = useState("all"); // Track selected timeline filter
  const [filteredTasks, setFilteredTasks] = useState([]); // Tasks filtered by timeline

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/assessments/assetAll", {
        headers: {
          "x-user-role": user?.roleid, // Custom header
        },
      });
      setTasks(response.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  // Filter tasks based on the selected timeline
  useEffect(() => {
    if (timelineFilter === "all") {
      setFilteredTasks(tasks);
    } else {
      setFilteredTasks(tasks.filter((task) => task.shiftType === timelineFilter));
    }
  }, [tasks, timelineFilter]);

  const handleFilterChange = (filter) => {
    setTimelineFilter(filter);
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Grid container className='task-container'>
      <Grid item sm={8} className='task-list'>
        <Box sx={{ p: 4, ml: 2, mr: 1 }}>
          <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left'>
            Tasks
          </Typography>
          <Grid container spacing={2}>
            <Grid item lg={10} xs={12}>
              <Box sx={{ mb: 4, display: "flex", gap: 3 }}>
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => handleFilterChange("all")}
                  sx={{ textTransform: "none", borderColor: timelineFilter === "all" ? "#1976d2" : "#ECECED" }}
                >
                  <span
                    style={{
                      backgroundColor: timelineFilter === "all" ? "#1976d2" : "#DCDCDE",
                      color: timelineFilter === "all" ? "white" : "black",
                      padding: "0 4px",
                      marginRight: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    {tasks.length}
                  </span>{" "}
                  All
                </Button>
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => handleFilterChange("Day Shift")}
                  sx={{ textTransform: "none", borderColor: timelineFilter === "Day Shift" ? "#1976d2" : "#ECECED" }}
                >
                  <span
                    style={{
                      backgroundColor: timelineFilter === "Day Shift" ? "#1976d2" : "#DCDCDE",
                      color: timelineFilter === "Day Shift" ? "white" : "black",
                      padding: "0 4px",
                      marginRight: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    {tasks.filter((task) => task.shiftType === "Day Shift").length}
                  </span>{" "}
                  Day
                </Button>
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => handleFilterChange("Night Shift")}
                  sx={{ textTransform: "none", borderColor: timelineFilter === "Night Shift" ? "#1976d2" : "#ECECED" }}
                >
                  <span
                    style={{
                      backgroundColor: timelineFilter === "Night Shift" ? "#1976d2" : "#DCDCDE",
                      color: timelineFilter === "Night Shift" ? "white" : "black",
                      padding: "0 4px",
                      marginRight: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    {tasks.filter((task) => task.shiftType === "Night Shift").length}
                  </span>{" "}
                  Night
                </Button>
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => handleFilterChange("Sleep In")}
                  sx={{ textTransform: "none", borderColor: timelineFilter === "Sleep In" ? "#1976d2" : "#ECECED" }}
                >
                  <span
                    style={{
                      backgroundColor: timelineFilter === "Sleep In" ? "#1976d2" : "#DCDCDE",
                      color: timelineFilter === "Sleep In" ? "white" : "black",
                      padding: "0 4px",
                      marginRight: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    {tasks.filter((task) => task.shiftType === "Sleep In").length}
                  </span>{" "}
                  Sleeping
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box className='scrollable-task-list'>
            <ManagerTaskCard tasks={filteredTasks} onTaskClick={setSelectedTask} />
          </Box>
        </Box>
      </Grid>

      <Grid item sm={4}>
        <Box className='detail-view'>
          {selectedTask && (
            <Box sx={{ p: 2, ml: 1, mr: 2 }}>
              {/* Task Title */}
              <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "black", mb: 1 }}>
                {selectedTask.taskName}
              </Typography>

              {/* Task Subtitle */}
              <Typography sx={{ fontSize: "12px", color: "black", mb: 1 }}>{selectedTask.description}</Typography>

              {/* Task Status */}
              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={8} md={8} xs={12}>
                  <Typography sx={{ fontSize: "14px" }}>Status</Typography>
                </Grid>
                <Grid item lg={4} md={4} xs={12}>
                  <Chip
                    variant='contained'
                    sx={{
                      fontSize: "12px",
                      backgroundColor: selectedTask.status === "completed" ? "#EAFFF2" : "#F2F6FF",
                      color: selectedTask.status === "completed" ? "green" : "blue",
                    }}
                    label={selectedTask.status}
                    size='small'
                  />
                </Grid>
              </Grid>

              {/* Assignee */}
              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={8} md={8} xs={12}>
                  <Typography sx={{ fontSize: "14px" }}>Assigned by</Typography>
                </Grid>
                <Grid item lg={4} md={4} xs={12}>
                  <Typography variant='body2'>{selectedTask.assignee}</Typography>
                </Grid>
              </Grid>

              {/* Date Created */}
              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={8} md={8} xs={12}>
                  <Typography sx={{ fontSize: "14px" }}>Date Created</Typography>
                </Grid>
                <Grid item lg={4} md={4} xs={12}>
                  <Typography variant='body2'>{format(new Date(selectedTask.created_at), "dd MMM yyyy")}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default ManagerTaskList;
